/* eslint-disable no-inner-declarations */
/* global data, serverParams */
import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import * as d3 from 'd3'

import { orderPitchTypeSubgroups } from 'bam-node-baseball-savant/utils/pitch-names'
import { loadTable } from '../table'
import CatcherVisual from './catcher-visual'
import calculateScales from './color-scales'

import SearchHandler, { createSearchElement } from './search'
import selected_store from './selected'

// Elements
const $legend = document.getElementById('catcher-block-legend')

function easeInEaseOut(t) {
    const sq = t * t
    return sq / (2 * (sq - t) + 1)
}

function addPolyArrays(data) {
    return data.map((d) => ({
        ...d,
        poly_x: [d.polynomial_x_1, d.polynomial_x_2, d.polynomial_x_3],
        poly_y: [d.polynomial_y_1, d.polynomial_y_2, d.polynomial_y_3],
        poly_z: [d.polynomial_z_1, d.polynomial_z_2, d.polynomial_z_3],
    }))
}

function getClosestAngle(angle) {
    const angles = selected_store.raw_data.map(
        ({ adjusted_angle }) => adjusted_angle
    )

    const diffs = angles.map((a) =>
        Math.min(Math.abs(a - angle), Math.abs(a - angle - Math.PI * 2))
    )

    const min_diff = d3.min(diffs)

    const new_idx = diffs.indexOf(min_diff)

    if (new_idx !== selected_store.selected_idx) {
        selected_store.setSelectedIdx(diffs.indexOf(min_diff))
        toggleSelected()
    }
}

function toggleSelected() {
    // Toggle Catcher Visual
    selected_store.raw_data.forEach(({ catcher }, i) => {
        const on = i === selected_store.selected_idx
        catcher.toggleSelected(on)
    })

    // Toggle Legend
    $legend.querySelectorAll('.search-result').forEach((d, i) => {
        const method = i === selected_store.selected_idx ? 'add' : 'remove'
        d.classList[method]('active-search-result')
    })
}

const raycaster = new THREE.Raycaster()
const pointerRelative = new THREE.Vector2(
    Number.MAX_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER
)

const pointer = new THREE.Vector2(
    Number.MAX_SAFE_INTEGER,
    Number.MAX_SAFE_INTEGER
)

const viz_group = new THREE.Group()

THREE.Object3D.DefaultUp.set(0.0, 0.0, 1.0)

const look_at = [0, 0, -8.5]

const $viz = document.getElementById('blocks-viz')

if ($viz) {
    let { offsetWidth, offsetHeight } = $viz

    let vizLeft
    let vizTop

    function setClientRect() {
        const $vizBoundingRect = $viz.getBoundingClientRect()

        vizLeft = $vizBoundingRect.left
        vizTop = $vizBoundingRect.top
    }

    setClientRect()

    function onPointerMove(event) {
        pointer.x = event.clientX - vizLeft
        pointer.y = event.clientY - vizTop

        // calculate pointer position in normalized device coordinates
        // (-1 to +1) for both components

        pointerRelative.x = ((event.clientX - vizLeft) / offsetWidth) * 2 - 1
        pointerRelative.y = -((event.clientY - vizTop) / offsetHeight) * 2 + 1
    }

    $viz.addEventListener('pointermove', onPointerMove)
    $viz.style.height = `${offsetHeight}px`
    $viz.style.width = `${offsetWidth}px`
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(0x234222) // darker green?
    // scene.background = new THREE.Color(0x348c31) // lighter green

    const camera = new THREE.PerspectiveCamera(
        75,
        offsetWidth / offsetHeight,
        0.1,
        1000
    )

    let color_scales

    const renderer = new THREE.WebGLRenderer({
        antialias: true,
        logarithmicDepthBuffer: true,
    })
    renderer.setSize(offsetWidth, offsetHeight)
    $viz.appendChild(renderer.domElement)

    let isUserRotating = false
    let controls

    function loadControls() {
        const is_enabled = window.innerWidth > 959

        if (!is_enabled) {
            return
        }

        controls = new OrbitControls(camera, renderer.domElement)
        // const axesHelper = new THREE.AxesHelper(15)
        // scene.add(axesHelper)
        controls.minDistance = 25
        controls.maxDistance = 45
        controls.minPolarAngle = 1
        controls.maxPolarAngle = 1
        // Before they toggle the pitches, don't allow them to zoom, because it's confusing
        controls.enableZoom = false
        controls.zoomSpeed = 0.3

        controls.enabled = window.innerWidth > 959

        controls.target.set(...look_at)

        controls.addEventListener('change', ({ target }) => {
            isUserRotating = true
            if (camera.position.z < 0) {
                camera.position.z = 0
            }
            if (selected_store.raw_data.length) {
                const angle = target.getAzimuthalAngle()
                getClosestAngle(angle)
            }
        })
    }

    loadControls()

    const initial_x_value = 0
    const initial_y_value = 24
    const initial_z_value = 6.5

    const start_angle = Math.atan2(initial_y_value, initial_x_value)

    const initial_ground_dist = Math.sqrt(
        initial_x_value ** 2 + initial_y_value ** 2
    )

    camera.position.x = initial_x_value
    camera.position.y = initial_y_value
    camera.position.z = initial_z_value

    camera.lookAt(...look_at)

    let current_color_scale = 'exp_pbwp_added'

    function getColor(bin) {
        const { scale, fn } = color_scales[current_color_scale] || {}

        if (!scale) {
            return 0xffffff
        }

        return scale(fn(bin))
    }

    let frame = 0
    let hovered_frame = 0

    // const light = new THREE.AmbientLight(0x404040, 0.5)
    // light.position.x = 0
    // light.position.y = 0
    // light.position.z = 10
    // scene.add(light)

    const light = new THREE.PointLight(0xffffff, 2, 100)
    light.position.x = 0
    light.position.y = 0
    light.position.z = 50
    scene.add(light)

    // const light_helper = new THREE.PointLightHelper(light);
    // scene.add(light_helper)

    // Ground?
    // const ground_geometry = new THREE.PlaneGeometry(1000, 1000);
    // const ground_material = new THREE.MeshBasicMaterial({ color: 0xff9a00, side: THREE.DoubleSide });
    // const ground_plane = new THREE.Mesh(ground_geometry, ground_material);
    // scene.add(ground_plane);

    function populateMetricsBox2(pitches, $div, isBinData) {
        if (!$div) {
            // the metrics box may have just been removed
            return
        }

        const has_pitches = pitches.length > 0

        const $total_pitches_value = $div.querySelector(
            '.total-pitches .stat-value'
        )
        const $pbwp_value = $div.querySelector('.pbwp .stat-value')
        const $x_pbwp_value = $div.querySelector('.x-pbwp .stat-value')
        const $blocks_added_value = $div.querySelector(
            '.actual-expected .stat-value'
        )
        const total_pitches = pitches.length
        $total_pitches_value.textContent = total_pitches.toLocaleString()

        const pbwp = pitches.filter((pitch) => pitch.is_runner_pbwp).length
        $pbwp_value.textContent = has_pitches ? pbwp : '--'

        const x_pbwp = d3.sum(pitches, (d) => d.x_runner_pbwp)
        $x_pbwp_value.textContent = has_pitches
            ? x_pbwp.toFixed(isBinData ? 1 : 0)
            : '--'

        // const x_pbwp_rate =
        //     d3.mean(pitches, (d) => d.x_runner_pbwp - d.is_runner_pbwp) * 40

        // $x_pbwp_value.textContent = has_pitches ? x_pbwp_rate.toFixed(2) : '--'

        const blocks_added = x_pbwp - pbwp
        let prefix = ''
        if (blocks_added > 0) {
            prefix = '+ '
            $blocks_added_value.classList.remove('negative-text')
            $blocks_added_value.classList.add('positive-text')
        } else {
            $blocks_added_value.classList.remove('positive-text')
            $blocks_added_value.classList.add('negative-text')
        }
        $blocks_added_value.textContent = has_pitches
            ? prefix + blocks_added.toFixed(isBinData ? 1 : 0)
            : '--'
    }

    function populateColorLegend() {
        if (!color_scales) return

        const { precision, scale, title } = color_scales[current_color_scale]
        const [min, max] = scale.domain()
        const mid = (max + min) / 2
        const min_mid = (min + mid) / 2
        const max_mid = (max + mid) / 2

        const vals = [min, min_mid, mid, max_mid, max]

        if (current_color_scale === 'exp_pbwp_added') {
            vals.reverse()
        }

        document.querySelectorAll('.scale-legend').forEach(($legendEl, i) => {
            // eslint-disable-next-line no-param-reassign
            $legendEl.style.backgroundColor = scale(vals[i])
        })

        if (current_color_scale !== 'exp_pbwp_added') {
            document.getElementById('scale-min').textContent = min.toFixed(0)
            document.getElementById('scale-max').textContent = max.toFixed(0)
        } else {
            // I'm brain dead, this could be way smarter.
            document.getElementById('scale-min').textContent =
                max > 0 ? `+${max.toFixed(precision)}` : max.toFixed(precision)
            document.getElementById('scale-max').textContent =
                min > 0 ? `+${min.toFixed(precision)}` : min.toFixed(precision)
        }

        document.getElementById('color-scale-title').textContent = title
    }

    function populatePitchTypes() {
        const pitch_types = selected_store.raw_data
            .flatMap(({ data }) => data)
            .map(({ api_pitch_type_subgroup }) => api_pitch_type_subgroup)

        const set_of_types = new Set(pitch_types)
        const ordered = orderPitchTypeSubgroups(Array.from(set_of_types))
        const $pitch_types = document.getElementById('ddlPitchType')

        const { value } = $pitch_types

        $pitch_types
            .querySelectorAll('option')
            .forEach((el, i) => i !== 0 && el.remove())

        ordered.forEach((type) => {
            $pitch_types.options[$pitch_types.options.length] = new Option(
                type,
                type
            )
        })

        if (value) {
            $pitch_types.value = value
        }
    }

    function getSelectedCatcher() {
        const catcher = selected_store.selected()?.catcher

        if (!catcher) {
            return null
        }

        return catcher
    }

    function getBinIntersection() {
        const boxes = getSelectedCatcher()?.bin_objects

        if (!boxes) {
            return []
        }

        const intersects = raycaster.intersectObjects(boxes)

        if (intersects.length > 0) {
            const group = intersects[0].object.parent
            return [group]
        }

        return []
    }

    const $pitchTooltip = document.getElementById('blocks-tooltip')
    const $pitchTooltipValue = document.getElementById(
        'blocks-tooltip-probability'
    )

    function placeTooltip(pitch) {
        const { x_runner_pbwp, is_runner_pbwp } = pitch.data

        const block_prob = (1 - x_runner_pbwp) * 100

        let textContent = `${Math.round(block_prob)}%`

        if (block_prob > 99.9) {
            textContent = '> 99.9%'
        } else if (block_prob > 99.5) {
            textContent = '> 99.5%'
        } else if (block_prob < 0.005) {
            textContent = '< 1%'
        }

        $pitchTooltipValue.textContent = textContent

        $pitchTooltip.style.borderColor = is_runner_pbwp ? '#f4511e' : '#555'

        // Add text content before getting the height/width
        const { width, height } = $pitchTooltip.getBoundingClientRect()

        $pitchTooltip.style.top = `${pointer.y - height / 2}px`
        if (pointerRelative.x > 0.25) {
            $pitchTooltip.style.left = `${pointer.x - width - 15}px`
        } else {
            $pitchTooltip.style.left = `${pointer.x + 15}px`
        }

        $pitchTooltip.style.visibility = 'visible'
    }

    function getBallIntersection() {
        const catcher = getSelectedCatcher()

        if (!catcher) {
            return null
        }

        const pitch = catcher.getHoveredPitch(raycaster)

        if (pitch) {
            pitch.highlight()

            placeTooltip(pitch)

            document.documentElement.style.cursor = 'pointer'
        } else {
            document.documentElement.style.cursor = 'auto'
            $pitchTooltip.style.visibility = 'hidden'
        }

        return null
    }

    let rotating = false
    let initial_rotation_angle = Math.PI / 2
    let initial_rotation_frame = frame
    let initial_rotation_z = camera.position.z
    let initial_rotation_ground_dist = initial_ground_dist

    function handleRotation() {
        const dist_change = initial_ground_dist - initial_rotation_ground_dist

        // For some reason did the calcuation based on the length, so use idx + 1
        const rotation_idx = selected_store.selected_idx + 1

        // const offset = (2 * Math.PI) / 36
        const offset = start_angle - Math.PI / 2
        const initial_angle = Math.PI / 2 + Math.PI / 3 + offset // Because length is not zero indexed, move it back a tick.
        const desired_angle = initial_angle - (rotation_idx * Math.PI) / 3

        const total_rotation = initial_rotation_angle - desired_angle

        const animation_pct = (frame - initial_rotation_frame) / 20

        const new_angle =
            initial_rotation_angle -
            total_rotation * easeInEaseOut(animation_pct)
        const new_dist =
            initial_rotation_ground_dist +
            dist_change * easeInEaseOut(animation_pct)

        const new_x = Math.cos(new_angle) * new_dist
        const new_y = Math.sin(new_angle) * new_dist

        const total_z_change = initial_z_value - initial_rotation_z
        const new_z =
            initial_rotation_z + total_z_change * easeInEaseOut(animation_pct)

        camera.position.set(new_x, new_y, new_z)
        camera.lookAt(...look_at)

        if (animation_pct >= 1) {
            rotating = false
        }
    }

    const xy_opacity_scale = d3
        .scaleLinear()
        .domain([1, 5])
        .range([0.5, 1])
        .clamp(true)
    const xz_opacity_scale = d3
        .scaleLinear()
        .domain([1, 10])
        .range([0.5, 1])
        .clamp(true)

    function animate() {
        raycaster.setFromCamera(pointerRelative, camera)

        const intersects = getBinIntersection()

        getBallIntersection()

        if (rotating) {
            handleRotation()
        }

        selected_store.raw_data.forEach(({ catcher }) => {
            catcher.xy_bin_objects.forEach((group) => {
                const box = group.getObjectByName('bin')
                const color = getColor(group.userData.bin)
                box.material.color.set(color)
                const { length } = group.userData.bin

                if (!length) {
                    box.material.opacity = 0.1
                } else {
                    box.material.opacity = xy_opacity_scale(length)
                }
            })

            catcher.xz_bin_objects.forEach((group) => {
                const box = group.getObjectByName('bin')
                const color = getColor(group.userData.bin)
                box.material.color.set(color)
                const { length } = group.userData.bin

                if (!length) {
                    box.material.opacity = 0.1
                } else {
                    box.material.opacity = xz_opacity_scale(length)
                }
            })

            catcher.shadows.forEach((shadow) => {
                // eslint-disable-next-line no-param-reassign
                shadow.visible = false
            })
        })

        const $active_search_result = document.querySelector(
            '.search-result.active-search-result'
        )

        intersects.forEach((group) => {
            const shadow = group.getObjectByName('bin_shadow')

            populateMetricsBox2(group.userData.bin, $active_search_result, true)

            if (group.userData.bin.length) {
                // bin.material.color.set(0xffac1c);
                // bin.material.color.set(0xff5f1f)
                shadow.visible = true
            }

            // Take note of last hover frame, to minimize flickering
            hovered_frame = frame
        })

        const selected_catcher = getSelectedCatcher()
        if (
            !intersects.length &&
            selected_store.raw_data.length &&
            frame - hovered_frame > 10 &&
            selected_catcher
        ) {
            populateMetricsBox2(
                selected_catcher.filtered_data,
                $active_search_result,
                false
            )
        }

        // Spin that camera round and round
        // camera.position.x = 20 * Math.cos(frame / 50)
        // camera.position.y = 10 * Math.sin(frame / 50) + 8
        // const x = Math.min(0, Math.max(-width, camera.position.x))
        // camera.lookAt(Math.abs(x), 0, 3)

        const catcher = getSelectedCatcher()
        if (catcher) {
            catcher.drawPitches()
        }
        // selected_store.raw_data.forEach(({ catcher }) => catcher.drawPitches())

        requestAnimationFrame(animate)
        frame += 1

        renderer.render(scene, camera)
    }
    animate()

    function updateOrbitalBounds() {
        const len = selected_store.raw_data.length

        // Update Azimuth Angles
        if (len !== 6) {
            const increment = (2 * Math.PI) / 6
            const init = -Math.PI + increment / 2
            if (controls) {
                controls.minAzimuthAngle = init - increment * len
                controls.maxAzimuthAngle = init
            }
        } else if (controls) {
            controls.minAzimuthAngle = undefined
            controls.maxAzimuthAngle = undefined
        }
    }

    function setVisualPositions() {
        selected_store.raw_data.forEach((d, i) => {
            /* eslint-disable no-param-reassign */
            d.catcher.group.position.x = 15 * Math.sin((Math.PI / 3) * i)
            d.catcher.group.position.y = 15 * Math.cos((Math.PI / 3) * i)
            d.catcher.group.rotation.z = (-Math.PI / 3) * i

            d.adjusted_angle = (-Math.PI / 3) * i + Math.PI
            /* eslint-enable no-param-reassign */
        })
    }

    function highlightSelectedScale() {
        document.getElementById('color-scale').classList.remove('hide')

        $legend.querySelectorAll('.stat-holder').forEach(($el) => {
            const method =
                $el.dataset.scale === current_color_scale ? 'add' : 'remove'
            $el.classList[method]('active-stat-holder')
        })
    }

    function addCatcherData(data, is_pitcher = false, year = false) {
        const { catcher_id, pitcher_id, player_name } = data[0]

        const player_id = is_pitcher ? pitcher_id : catcher_id

        const catcher = new CatcherVisual({
            data: addPolyArrays(data),
            is_team_type: serverParams.is_team_type,
            player_id,
            player_name,
            year,
        })
        // const spot_light_helper = new THREE.SpotLightHelper(catcher.spot_light)
        // scene.add(spot_light_helper)

        // const point_light_helper = new THREE.PointLightHelper(catcher.point_light)
        // scene.add(point_light_helper)

        return catcher
    }

    function recalculateScales() {
        const bins = selected_store.raw_data
            .map((d) => [...d.catcher.xy_bins_flat, ...d.catcher.xz_bins_flat])
            .flat()
        color_scales = calculateScales(bins)

        populateColorLegend()
    }

    scene.add(viz_group)

    function removeCatcherData(datum, $el) {
        if (!$el) {
            return
        }

        const idx = selected_store.findIndex(datum)

        // Update selected idx
        if (selected_store.raw_data.length === 1) {
            selected_store.setSelectedIdx(undefined)
        } else if (selected_store.selected_idx === idx) {
            selected_store.setSelectedIdx(0)
        } else if (selected_store.selected_idx > idx) {
            selected_store.incrementSelectedIdx(-1)
        }

        const to_remove = selected_store.remove(datum)

        if (to_remove) {
            viz_group.remove(to_remove.catcher.group)
        }

        if (selected_store.raw_data.length) {
            initializeRotation()
        }
        updateOrbitalBounds()
        setVisualPositions()

        // Remove legend information
        $el.remove()

        toggleSelected()
    }

    const $pitch_type_select = document.getElementById('ddlPitchType')
    $pitch_type_select.addEventListener('change', changeFilters)

    const $pitch_type_quality = document.getElementById('ddlPitchQuality')
    $pitch_type_quality.addEventListener('change', changeFilters)
    $pitch_type_quality.addEventListener('change', (e) => {
        // eslint-disable-next-line no-use-before-define
        const $legendItem = $pitch_legend.querySelector(
            `.legend-${e.target.value}`
        )

        document.querySelectorAll('.difficulty-legend').forEach(($el) => {
            if ($el === $legendItem) {
                $el.classList.remove('hide')
            } else {
                $el.classList.add('hide')
            }
        })
    })

    let is_pbwp = null
    function changeFilters() {
        const difficulty = [...$pitch_type_quality.selectedOptions]
            .map(({ value }) => Number(value))
            .filter((value) => value !== 0)

        // No longer is multiple, but maybe someday?
        const pitch_type = [...$pitch_type_select.selectedOptions]
            .map(({ value }) => value)
            .filter((value) => value !== '')

        selected_store.raw_data.forEach(({ catcher }) => {
            catcher.filterPitches({
                difficulty,
                pitch_type,
                is_pbwp,
            })
        })
    }

    // populateColorLegend()
    // populateNameAndImage()

    // populatePitchTypes()

    // const $pitch_visual_select = document.getElementById('pitch-visuals')
    // $pitch_visual_select.addEventListener('change', (e) => {
    //     const hide = e.target.value === 'none'
    //     selected_store.raw_data.forEach(({ catcher }) => {
    //         if (hide) {
    //             catcher.hidePitches()
    //         } else {
    //             catcher.showPitches(e.target.value)
    //         }
    //     })
    // })

    const $pitch_legend = document.getElementById('supplemental-legend')
    document.getElementById('show-pitches').addEventListener('click', () => {
        if (controls) {
            controls.enableZoom = true
        }
        $pitch_legend.querySelector('.pitch-legend').classList.remove('hide')
        const catcher = getSelectedCatcher()

        if (catcher) {
            catcher.togglePitches()
        }
    })

    function dimType(type) {
        document
            .querySelectorAll(`.supplemental-legend-item-${type}`)
            .forEach(($el) => {
                // eslint-disable-next-line no-param-reassign
                $el.style.opacity = 0.3
            })
    }

    function highlightType(type) {
        document
            .querySelectorAll(`.supplemental-legend-item-${type}`)
            .forEach(($el) => {
                // eslint-disable-next-line no-param-reassign
                $el.style.opacity = 1
            })
    }

    $pitch_legend.addEventListener('click', () => {
        switch (is_pbwp) {
            case null:
                is_pbwp = true
                highlightType('pbwp')
                dimType('block')
                break
            case true:
                is_pbwp = false
                highlightType('block')
                dimType('pbwp')
                break
            default:
                highlightType('block')
                highlightType('pbwp')
                is_pbwp = null
                break
        }
        changeFilters()
    })

    const $input = document.querySelector('.table-search')
    const $resultWrapper = document.querySelector('.blocks-search-results-2')

    // eslint-disable-next-line import/prefer-default-export
    const searchHandler = new SearchHandler(
        $input,
        $resultWrapper,
        $legend,
        data,
        fetchAndDisplayData
    )

    // // Search
    // document
    //     .querySelector('#blocks-search input')
    //     .addEventListener('input', (e) => {
    //         const search = e.target.value
    //             .normalize('NFD')
    //             .replace(/[\u0300-\u036f]/g, '')
    //             .toLowerCase()

    //         const filtered = data
    //             .filter((d) => {
    //                 const catcher_name = d.name_display_last_first
    //                     .normalize('NFD')
    //                     .replace(/[\u0300-\u036f]/g, '')
    //                     .toLowerCase()

    //                 return (
    //                     catcher_name.includes(search) &&
    //                     !selected_store.includes(d.entity_id)
    //                 )
    //             })
    //             .sort((a, b) => b.diff_runner_pbwp - a.diff_runner_pbwp)
    //             .slice(0, 4)

    //         // Clear current results
    //         const $search_wrapper = document.getElementById('blocks-search-results')
    //         $search_wrapper.innerHTML = ''

    //         // Add new results
    //         filtered.forEach((d) => {
    //             const $result = createSearchElement(d)
    //             $search_wrapper.appendChild($result)
    //         })
    //     })

    // We use the initial information to interpolate to where we want to be
    function initializeRotation() {
        const { x, y, z } = camera.position
        initial_rotation_angle = Math.atan2(y, x)
        initial_rotation_frame = frame
        initial_rotation_z = z
        initial_rotation_ground_dist = Math.sqrt(x ** 2 + y ** 2)
        rotating = true
    }

    async function fetchAndDisplayData(datum, set_selected = true) {
        selected_store.add(datum)

        // Save the index before adding the raw data
        const i = selected_store.players.length - 1

        const { splitYears } = serverParams

        const parameters = {
            game_type: serverParams.game_type,
            season_start: splitYears
                ? datum.start_year
                : serverParams.season_start,
            season_end: splitYears ? datum.start_year : serverParams.season_end,
            split: serverParams.spitYears ? 1 : 0,
            team:
                serverParams.team === 'split'
                    ? datum.key_team_id
                    : serverParams.team || '',
            type: serverParams.entity_code,
            with_team_only: serverParams.with_team_only ? 1 : 0,
        }
        const urlParams = new URLSearchParams(parameters)

        const { data } = await fetch(
            `/leaderboard/services/catcher-blocking/${
                datum.entity_id
            }?${urlParams.toString()}`
        ).then((d) => d.json())

        const catcher = await addCatcherData(
            data,
            serverParams.entity_code === 'Pit',
            splitYears && datum.start_year
        )

        selected_store.addRawData({
            player_id: datum.entity_id,
            row: datum,
            catcher,
            data: addPolyArrays(data),
        })

        setVisualPositions()

        // calculate color scales based on current bins
        recalculateScales()

        viz_group.add(catcher.group)

        updateOrbitalBounds()
        highlightSelectedScale()

        populatePitchTypes()
        changeFilters()

        if (set_selected) {
            selected_store.setSelectedIdx(i)
            toggleSelected()
            initializeRotation()
        }

        return data
    }

    function getInitialVisualizedPlayers() {
        const params = new URLSearchParams(window.location.search)

        const keys = params.get('players')

        if (!keys) {
            return { players: [data[0]], idx: 0 }
        }

        const keysArray = keys
            .split(',')
            .map((d) => d.split('-').map((y) => Number(y)))

        const players = keysArray.map(([entity_id, start_year, team]) =>
            findDatum(entity_id, start_year, team)
        )

        return {
            players,
            idx: Number(params.get('selected_idx')) || 0,
        }
    }

    async function initialize() {
        const { players, idx } = getInitialVisualizedPlayers()

        await Promise.all(
            players.map(async (d) => {
                const $result = createSearchElement(d)
                searchHandler.moveResult($result)
                await fetchAndDisplayData(d, false)
                removeSpinner($result)
            })
        )
        // reload table to fix buttons
        loadTable(getFilteredData())

        // Set desired idx
        selected_store.setSelectedIdx(idx)
        toggleSelected()
        initializeRotation()
    }

    function getFilteredData() {
        const search = document.querySelector('.table-search').value
        return searchHandler.getFilteredData(search)
    }

    initialize()

    function removeSpinner($result) {
        // hide spinner
        $result.querySelector('.spinner').classList.add('hide')
    }

    $legend.addEventListener('click', (e) => {
        const $el = e.target.closest('.search-result')
        const $remove = e.target.closest('.search-result-remove')

        if (!$el) {
            return
        }

        if ($remove) {
            const { id, team, year } = $el.dataset
            const datum = findDatum(id, year, team)
            removeCatcherData(datum, $el)
            loadTable(getFilteredData())
            e.stopPropagation()
            return
        }

        // find out what index $el is inside #legend
        const idx = Array.from($legend.children).indexOf($el)

        if (selected_store.selected_idx !== idx) {
            selected_store.setSelectedIdx(idx)
            toggleSelected()
            initializeRotation()
        }
    })

    $legend.addEventListener('click', (e) => {
        const $stat_holder = e.target.closest('.stat-holder')

        if (!$stat_holder) {
            return
        }

        const scale = $stat_holder.getAttribute('data-scale')
        current_color_scale = scale

        highlightSelectedScale()

        populateColorLegend()
    })

    window.addEventListener('resize', () => {
        $viz.style.height = 'auto'
        $viz.style.width = 'auto'
        offsetWidth = $viz.offsetWidth
        offsetHeight = $viz.offsetHeight
        $viz.style.height = `${offsetHeight}px`
        $viz.style.width = `${offsetWidth}px`
        setClientRect()
        camera.aspect = offsetWidth / offsetHeight
        camera.updateProjectionMatrix()
        renderer.setSize(offsetWidth, offsetHeight)
    })

    window.addEventListener('scroll', () => {
        setClientRect()
    })

    function findDatum(playerId, year, team) {
        return data.find(
            (d) =>
                d.entity_id === Number(playerId) &&
                d.start_year === Number(year) &&
                d.key_team_id === Number(team)
        )
    }

    document.addEventListener('click', async ({ target }) => {
        // Add Visual
        if (target && target.classList.contains('compare-btn')) {
            // Get pertinent information
            const { id, year, team } = target.dataset

            // Find row
            const datum = findDatum(id, year, team)

            if (selected_store.includes(datum)) {
                // Already loaded! Go to Visual
                $viz.scrollIntoView({ behavior: 'smooth' })

                // Rotate to correct position
                const idx = selected_store.findIndex(datum)
                selected_store.setSelectedIdx(idx)
                toggleSelected()
                initializeRotation()
            } else {
                // remove the first catcher if we have 6 already
                if (selected_store.players.length === 6) {
                    const to_remove = selected_store.players[0]
                    const $el = $legend.querySelector('.search-result')

                    removeCatcherData(to_remove, $el)
                }

                // eslint-disable-next-line no-param-reassign
                target.textContent = 'Loading...'

                // Add to legend
                const $result = createSearchElement(datum)
                $legend.appendChild($result)

                // show spinner
                $result.querySelector('.spinner').classList.remove('hide')
                // hide +/- blocks
                $result
                    .querySelector('.search-result-value')
                    .classList.add('hide')

                // fetch data and add to store
                await fetchAndDisplayData(datum)

                // hide spinner
                $result.querySelector('.spinner').classList.add('hide')

                // Update table
                loadTable(getFilteredData())
            }
        }
    })

    // // Sample
    // THREE.Object3D.prototype.getObjectsByName = function (name, result = []) {
    //     // check the current object

    //     if (this.name === name) result.push(this)

    //     // check children

    //     for (let i = 0, l = this.children.length; i < l; i++) {
    //         const child = this.children[i]

    //         child.getObjectsByName(name, result)
    //     }

    //     return result
    // }

    renderer.domElement.addEventListener('click', () => {
        if (isUserRotating) {
            isUserRotating = false
            return
        }

        const catcher = getSelectedCatcher()
        if (!catcher) {
            return
        }

        const pitch = catcher.getHoveredPitch(raycaster)

        if (pitch) {
            pitch.openVideo()
        }
    })
}
